import { Injectable } from '@angular/core';
import { INavData } from '@coreui/angular-pro';
import { CredoMenuService } from 'credo';

/**
 * fac3less menu = the standard credo menu minus the items that no longer apply
 * to fac3less (User Statistics, Earnings Statistics, User Tree, Community,
 * My Payouts). Extends the credo menu so it stays in sync with future changes;
 * only the listed user-facing URLs are filtered out (admin items are kept).
 */
@Injectable({ providedIn: 'root' })
export class Fac3lessMenuService extends CredoMenuService {
  protected override createNavItems(lang: string, t: (key: string) => string): INavData[] {
    const removed = [
      `/${lang}/user-statistics`,
      `/${lang}/earnings-statistics`,
      `/${lang}/user-tree`,
//      `/${lang}/community`,
      `/${lang}/my-payouts`,
    ];
    const items = super.createNavItems(lang, t).filter(item => !removed.includes(item.url as string));

    // User-facing ViaCarte card area (gated server-side by card ownership).
    // Placed right after the "Information" item.
    const mycards: INavData = {
      name: t('mycards.menu'),
      url: `/${lang}/mycards`,
      iconComponent: { name: 'cil-credit-card' },
    };
    const infoIdx = items.findIndex(item => item.url === `/${lang}/media/info`);
    if (infoIdx >= 0) {
      items.splice(infoIdx + 1, 0, mycards);
    } else {
      items.push(mycards);
    }

    // Appointments — the central calendar. Visible to every member; next to
    // Bookings so it does not land below the admin block.
    const appointments: INavData = {
      name: t('appointments.menu'),
      url: `/${lang}/appointments`,
      iconComponent: { name: 'cil-calendar' },
    };
    const bookingsIdx = items.findIndex(item => item.url === `/${lang}/bookings`);
    if (bookingsIdx >= 0) {
      items.splice(bookingsIdx + 1, 0, appointments);
    } else {
      items.push(appointments);
    }

    // ViaCarte card issuing admin (only fac3less + Global Health trigger ViaCarte).
    items.push({
      name: t('viacarte_admin.menu'),
      url: `/${lang}/admin/viacarte`,
      iconComponent: { name: 'cil-credit-card' },
      badge: { color: 'danger', text: 'ADMIN' },
      attributes: { 'data-visible': 'isAdmin' },
    });

    // Card partner programme: who is owed a card, and who the partner asked about
    // and got nothing for. Placed next to Card Accounts rather than appended, so it
    // does not land below the fold at the bottom of a long admin menu.
    const entitlements: INavData = {
      name: t('card_entitlements.menu'),
      url: `/${lang}/admin/card-entitlements`,
      iconComponent: { name: 'cil-credit-card' },
      badge: { color: 'danger', text: 'ADMIN' },
      attributes: { 'data-visible': 'isAdmin' },
    };
    const cardAccountsIdx = items.findIndex(item => item.url === `/${lang}/admin/card-accounts`);
    if (cardAccountsIdx >= 0) {
      items.splice(cardAccountsIdx + 1, 0, entitlements);
    } else {
      items.push(entitlements);
    }

    // Card partner analytics (monthly summary + cardholder ranking), next to the
    // entitlements it complements.
    const cardPartner: INavData = {
      name: t('card_partner_admin.menu'),
      url: `/${lang}/admin/card-partner`,
      iconComponent: { name: 'cil-chart' },
      badge: { color: 'danger', text: 'ADMIN' },
      attributes: { 'data-visible': 'isAdmin' },
    };
    const entitlementsIdx = items.findIndex(item => item.url === `/${lang}/admin/card-entitlements`);
    if (entitlementsIdx >= 0) {
      items.splice(entitlementsIdx + 1, 0, cardPartner);
    } else {
      items.push(cardPartner);
    }

    // Card partner credentials. Super admin only, matching the backend: a token
    // issued here reads cardholder names, addresses and dates of birth.
    items.push({
      name: t('partner_tokens.menu'),
      url: `/${lang}/admin/partner-tokens`,
      iconComponent: { name: 'cil-lock-locked' },
      badge: { color: 'danger', text: 'SUPER' },
      attributes: { 'data-visible': 'isSuperAdmin' },
    });

    // Sales Toolkit: the folder members see first under Information. Plain
    // admin, like the backend; every write is bound to this brand's folder.
    items.push({
      name: t('sales_toolkit.admin_menu'),
      url: `/${lang}/admin/sales-toolkit`,
      iconComponent: { name: 'cil-folder-open' },
      badge: { color: 'danger', text: 'ADMIN' },
      attributes: { 'data-visible': 'isAdmin' },
    });

    items.push({
      name: t('appointments.admin_menu'),
      url: `/${lang}/admin/appointments`,
      iconComponent: { name: 'cil-calendar' },
      badge: { color: 'danger', text: 'ADMIN' },
      attributes: { 'data-visible': 'isAdmin' },
    });

    return items;
  }
}
